import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAddFunds } from '@/hooks/api/useBilling';
import { ErrorMessage, LoadingSpinner } from '@/components/ui';

const paymentSchema = z.object({
  amount: z
    .number()
    .min(5, 'Minimum payment is $5.00')
    .max(10000, 'Maximum payment is $10,000.00'),
  payment_method_id: z.string().min(1, 'Please select a payment method'),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

interface PaymentFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
}

const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500];

export const PaymentForm: React.FC<PaymentFormProps> = ({
  onSuccess,
  onCancel,
  className = '',
}) => {
  const { mutateAsync: addFunds } = useAddFunds();
  const [customAmount, setCustomAmount] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      payment_method_id: 'default', // TODO: Get from payment methods list
    },
  });

  const handlePresetClick = (amount: number) => {
    setSelectedPreset(amount);
    setCustomAmount('');
    setValue('amount', amount);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedPreset(null);
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setValue('amount', numValue);
    }
  };

  const onSubmit = async (data: PaymentFormData) => {
    try {
      await addFunds({
        amount: data.amount,
        payment_method_id: data.payment_method_id,
        description: `Add funds: $${data.amount.toFixed(2)}`,
      });

      setSuccessMessage(`Successfully added $${data.amount.toFixed(2)} to your account!`);
      setTimeout(() => {
        setSuccessMessage('');
        onSuccess?.();
      }, 2000);
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Payment failed. Please try again.';
      setError('root', {
        type: 'manual',
        message: errorMessage,
      });
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Funds</h2>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      {errors.root && (
        <ErrorMessage message={errors.root.message || 'An error occurred'} className="mb-4" />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Preset Amounts */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Select Amount</label>
          <div className="grid grid-cols-3 gap-3">
            {PRESET_AMOUNTS.map((amount) => (
              <button
                key={amount}
                type="button"
                onClick={() => handlePresetClick(amount)}
                className={`
                  px-4 py-3 border-2 rounded-lg text-sm font-medium transition-all
                  ${
                    selectedPreset === amount
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-300 hover:border-indigo-400 text-gray-700'
                  }
                `}
              >
                ${amount}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div>
          <label htmlFor="custom-amount" className="block text-sm font-medium text-gray-700 mb-2">
            Or Enter Custom Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              id="custom-amount"
              type="number"
              step="0.01"
              min="5"
              max="10000"
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="0.00"
            />
          </div>
          {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
        </div>

        {/* Payment Method - Placeholder for Stripe integration */}
        <div>
          <label
            htmlFor="payment_method_id"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Payment Method
          </label>
          <div className="p-4 border border-gray-300 rounded-md bg-gray-50">
            <p className="text-sm text-gray-600">
              Stripe payment integration will be configured here.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              For now, using default payment method.
            </p>
          </div>
          <input type="hidden" {...register('payment_method_id')} />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSubmitting || (!selectedPreset && !customAmount)}
            className="
              flex-1 px-6 py-3 bg-indigo-600 text-white rounded-md
              hover:bg-indigo-700 transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2
              font-medium
            "
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Processing...</span>
              </>
            ) : (
              'Add Funds'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="
                px-6 py-3 border border-gray-300 text-gray-700 rounded-md
                hover:bg-gray-50 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Info Section */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Payment Information</h3>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>Minimum payment: $5.00</li>
          <li>Maximum payment: $10,000.00</li>
          <li>Funds are added instantly to your account</li>
          <li>All transactions are secure and encrypted</li>
        </ul>
      </div>
    </div>
  );
};
